/* eslint-disable @stylistic/indent */
/* eslint-disable @stylistic/operator-linebreak */
/* eslint-disable @stylistic/function-paren-newline */
import { useCallback, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { AssemblyOptionColorPalette } from "../../../../powershadesApiTypes";
import type {
    AssemblyStore,
    IndoorShadeOptionsDict,
    PortalShadeOption,
    PortalShadeOptionItem,
} from "../../../../Store/entities/assemblies/types";

type PaletteDefaults = {
    name: string;
    default_colors: Record<string, string>;
};

type PaletteListProvider = (assembly: AssemblyStore) => AssemblyOptionColorPalette[] | undefined;

type PaletteDefaultsProvider = (
    assembly: AssemblyStore,
) => Array<{
    name: string;
    default_colors?: Record<string, string>;
    defaultColors?: Record<string, string>;
}> | undefined;

type UseMassEditIndoorColorOptionsArgs = {
    selectedAssemblies: AssemblyStore[];
    workingAssembly: AssemblyStore;
    setWorkingAssembly: Dispatch<SetStateAction<AssemblyStore>>;
    shadeOptionsBase: IndoorShadeOptionsDict;
    getFullPaletteList: PaletteListProvider;
    colorOptions: PaletteDefaultsProvider;
};

type UseMassEditIndoorColorOptionsResult = {
    sharedColorPartOptions: AssemblyOptionColorPalette[];
    sharedPaletteOptions: PaletteDefaults[];
    colorPaletteOption: PortalShadeOption<string>;
    colorOptionsTyped: PortalShadeOption<string>[];
    shadeOptions: IndoorShadeOptionsDict;
    computePaletteColors: (
        assembly: AssemblyStore,
        paletteName: string,
    ) => Record<string, string>;
};

const intersectNames = (lists: string[][]): string[] => {
    if (lists.length === 0) return [];
    const [first, ...rest] = lists;
    const uniqueFirst = Array.from(new Set(first));
    if (rest.length === 0) return uniqueFirst;
    return uniqueFirst.filter((name) => rest.every((list) => list.includes(name)));
};

const extractDefaultColorRecord = (
    paletteDefaults: Record<string, string> | undefined,
    fallbackDefaults: Record<string, string> | undefined,
): Record<string, string> => ({
    ...(paletteDefaults ?? {}),
    ...(fallbackDefaults ?? {}),
});

// Grab common color/palette options across selected assemblies
const computeSharedColorData = (
    assemblies: AssemblyStore[],
    getFullPaletteList: PaletteListProvider,
    colorOptions: PaletteDefaultsProvider,
): {
    sharedColorPartOptions: AssemblyOptionColorPalette[];
    sharedPaletteOptions: PaletteDefaults[];
} => {
    // TODO: Only grabs options from shared properties, should gather all possible options
    if (assemblies.length === 0) {
        return {
            sharedColorPartOptions: [],
            sharedPaletteOptions: [],
        };
    }

    const paletteLists = assemblies.map((assy) => getFullPaletteList(assy) ?? []);
    const defaultPaletteLists = assemblies.map((assy) => colorOptions(assy) ?? []);

    const sharedPartNames = intersectNames(
        paletteLists.map((list) => list.map((palette) => palette.name)),
    );

    const colorPartOptions = sharedPartNames
        .map((name) => {
            const intersectedChoices =
                paletteLists.reduce<string[] | null>((acc, list) => {
                    const palette = list.find((p) => p.name === name);
                    if (!palette) return [];
                    const paletteChoices = palette.choices ?? [];
                    if (acc === null) return Array.from(new Set(paletteChoices));
                    const choiceSet = new Set(paletteChoices);
                    return acc.filter((choice) => choiceSet.has(choice));
                }, null) ?? [];

            const uniqueChoices = Array.from(new Set(intersectedChoices));
            if (uniqueChoices.length === 0) return null;

            const isRequired = paletteLists.every(
                (list) => list.find((p) => p.name === name)?.is_required_choice,
            );

            return {
                name,
                choices: uniqueChoices,
                is_required_choice: isRequired ?? false,
            } as AssemblyOptionColorPalette;
        })
        .filter((palette): palette is AssemblyOptionColorPalette => !!palette);

    const sharedPartNameSet = new Set(colorPartOptions.map((palette) => palette.name));

    const sharedPaletteNames = intersectNames(
        defaultPaletteLists.map((list) => list.map((palette) => palette.name)),
    );

    const paletteOptions = sharedPaletteNames
        .map((name) => {
            const palettes = defaultPaletteLists.map((list) => list.find((pal) => pal.name === name));
            if (palettes.some((pal) => !pal)) return null;

            const defaultColorRecords = palettes.map((pal) =>
                extractDefaultColorRecord(
                    (pal as any)?.default_colors,
                    (pal as any)?.defaultColors,
                ),
            );

            const mergedDefaults: Record<string, string> = {};
            sharedPartNameSet.forEach((partName) => {
                const firstVal = defaultColorRecords[0]?.[partName];
                if (!firstVal) return;
                const matches = defaultColorRecords.every(
                    (record) => record?.[partName] === firstVal,
                );
                if (matches) {
                    mergedDefaults[partName] = firstVal;
                }
            });

            if (Object.keys(mergedDefaults).length === 0) return null;

            return {
                name,
                default_colors: mergedDefaults,
            };
        })
        .filter((palette): palette is PaletteDefaults => !!palette);

    return {
        sharedColorPartOptions: colorPartOptions,
        sharedPaletteOptions: paletteOptions,
    };
};

// Grab default colors individual assembly
const computePaletteColors = (
    assembly: AssemblyStore,
    paletteName: string,
    getFullPaletteList: PaletteListProvider,
    colorOptions: PaletteDefaultsProvider,
): Record<string, string> => {
    if (!paletteName || paletteName === "Custom Palette") {
        return { ...(assembly.colors ?? {}) } as Record<string, string>;
    }

    const paletteList = getFullPaletteList(assembly) ?? [];
    const defaultPaletteOptions = colorOptions(assembly) ?? [];
    const selectedPalette = defaultPaletteOptions.find((palette) => palette.name === paletteName);
    const defaultColorMap = extractDefaultColorRecord(
        (selectedPalette as any)?.default_colors,
        (selectedPalette as any)?.defaultColors,
    );

    return paletteList.reduce<Record<string, string>>((acc, palette) => {
        const choices = Array.isArray(palette?.choices) ? palette.choices : [];
        let colorValue = defaultColorMap[palette.name] ?? "";
        if (colorValue === "Missing Default Color" || (colorValue && !choices.includes(colorValue))) {
            const fallback = choices.find((choice) => typeof choice === "string" && choice.trim() !== "") ?? "";
            colorValue = fallback;
        }
        acc[palette.name] = colorValue ?? "";
        return acc;
    }, {});
};

export const useMassEditIndoorColorOptions = (
    {
        selectedAssemblies,
        workingAssembly,
        setWorkingAssembly,
        shadeOptionsBase,
        getFullPaletteList,
        colorOptions,
    }: UseMassEditIndoorColorOptionsArgs,
): UseMassEditIndoorColorOptionsResult => {
    const { sharedColorPartOptions, sharedPaletteOptions } = useMemo(
        () => {
            const assembliesForColors = selectedAssemblies.length > 0
                ? selectedAssemblies
                : [workingAssembly];
            return computeSharedColorData(assembliesForColors, getFullPaletteList, colorOptions);
        },
        [selectedAssemblies, workingAssembly, getFullPaletteList, colorOptions],
    );

    const computePaletteColorsMemo = useCallback(
        (assembly: AssemblyStore, paletteName: string) =>
            computePaletteColors(assembly, paletteName, getFullPaletteList, colorOptions),
        [colorOptions, getFullPaletteList],
    );

    // Initialize palette options with palettes shared across all selected assemblies
    const basePaletteOptions = useMemo(
        () => (shadeOptionsBase?.ColorPalettes?.options ?? []) as PortalShadeOptionItem<string>[],
        [shadeOptionsBase],
    );

    const customPaletteOption = useMemo(
        () => basePaletteOptions.find(
            (opt) => typeof opt?.value === "string" && opt.value.toLowerCase() === "custom palette",
        ),
        [basePaletteOptions],
    );

    const sharedPaletteOptionItems = useMemo(() => {
        const items: PortalShadeOptionItem<string>[] = sharedPaletteOptions.map((palette) => ({
            value: palette.name,
            label: palette.name,
            title: palette.name,
            default_colors: palette.default_colors,
        }));

        if (customPaletteOption && !items.some((opt) => opt.value === customPaletteOption.value)) {
            // Ensure the synthetic "Custom Palette" entry stays available
            items.push(customPaletteOption);
        }

        return items;
    }, [customPaletteOption, sharedPaletteOptions]);

    const colorPaletteOption: PortalShadeOption<string> = useMemo(() => ({
        varName: "color_palette",
        options: sharedPaletteOptionItems,
        label: "Color Palette",
        error: [],
        getSelectedOption: () =>
            sharedPaletteOptionItems.find((opt) => opt.value === workingAssembly.palette_color)
            ?? sharedPaletteOptionItems.find((opt) => opt.value === "Custom Palette")
            ?? (sharedPaletteOptionItems[0]
                ?? ({
                    value: "Custom Palette",
                    label: "Custom Palette",
                    title: "Custom Palette",
                    default_colors: {},
                } as PortalShadeOptionItem<string>)),
        setValue: (value) => {
            const selectedDefaults = sharedPaletteOptions.find((opt) => opt.name === value)?.default_colors ?? {};
            setWorkingAssembly((prev) => {
                if (value === "Custom Palette") {
                    return {
                        ...prev,
                        hardware_color: value,
                        palette_color: value,
                    };
                }

                const updatedColors = { ...(prev.colors ?? {}) } as Record<string, string>;
                sharedColorPartOptions.forEach((palette) => {
                    const defaultColor = selectedDefaults[palette.name];
                    if (defaultColor && palette.choices.includes(defaultColor)) {
                        updatedColors[palette.name] = defaultColor;
                    } else {
                        const currentColor = updatedColors[palette.name];
                        const hasCurrent = typeof currentColor === "string" && currentColor !== "";
                        const currentIsValid = hasCurrent && palette.choices.includes(currentColor);
                        if (!currentIsValid) {
                            const fallback = palette.choices.find((choice) => !!choice);
                            if (fallback) {
                                updatedColors[palette.name] = fallback;
                            } else {
                                delete updatedColors[palette.name];
                            }
                        }
                    }
                });

                return {
                    ...prev,
                    hardware_color: value,
                    palette_color: value,
                    colors: updatedColors,
                };
            });
        },
        clearValue: () => setWorkingAssembly((prev) => ({
            ...prev,
            hardware_color: "Custom Palette",
            palette_color: "Custom Palette",
        })),
        active: sharedPaletteOptionItems.length > 0,
        info: [],
        valuePopup: false,
        getValue: () => workingAssembly.palette_color,
    }), [
        setWorkingAssembly,
        sharedPaletteOptionItems,
        sharedPaletteOptions,
        sharedColorPartOptions,
        workingAssembly.palette_color,
    ]);

    const colorOptionsTyped = useMemo(
        () => sharedColorPartOptions.map((palette) => {
            const newPalette: PortalShadeOption<string> = {
                varName: palette.name,
                options: palette.choices.map((color) => ({
                    value: color,
                    label: color,
                    title: color,
                })),
                label: palette.name,
                error: [],
                getSelectedOption: () => {
                    const item = palette.choices.find((color) => workingAssembly.colors[palette.name] === color);
                    return {
                        label: item ?? "Missing Default Color",
                        value: item ?? "Missing Default Color",
                        title: item ?? "Missing Default Color",
                    };
                },
                setValue: (value) => setWorkingAssembly((prev) => ({
                    ...prev,
                    colors: { ...prev.colors, [palette.name]: value },
                })),
                clearValue: () => setWorkingAssembly((prev) => ({
                    ...prev,
                    colors: { ...prev.colors, [palette.name]: "" },
                })),
                active: true,
                info: [],
                valuePopup: false,
                getValue: () => workingAssembly.colors[palette.name] ?? "",
                colorShow: true,
            };
            return newPalette;
        }),
        [setWorkingAssembly, sharedColorPartOptions, workingAssembly.colors],
    );

    const shadeOptions = useMemo<IndoorShadeOptionsDict>(
        () => ({
            ...shadeOptionsBase,
            ColorPalettes: colorPaletteOption,
            Colors: colorOptionsTyped,
        }),
        [shadeOptionsBase, colorPaletteOption, colorOptionsTyped],
    );

    return {
        sharedColorPartOptions,
        sharedPaletteOptions,
        colorPaletteOption,
        colorOptionsTyped,
        shadeOptions,
        computePaletteColors: computePaletteColorsMemo,
    };
};

export type {
    PaletteDefaults,
    UseMassEditIndoorColorOptionsArgs,
    UseMassEditIndoorColorOptionsResult,
};
